// src/components/Hero.js

import React from 'react'
import { useTranslation } from 'react-i18next'
import MessageSection from './ShortMessage';
import NotificationMarquee from './NotificationMarquee';
import StructuredData from './StructuredData';
import '../styles/Hero.scss'
import ContactUs from './ContactUs';
import Testimonials from './Testimonial';
import AboutSchool from './About';
import FacebookPagePosts from './FacebookPost';
import useScrollReveal from '../hooks/useScrollReveal';
import {
  GraduationCap, Users, Award, BookOpen,
  Star, Shield, Palette, Monitor, Heart,
  Trophy, CheckCircle, ArrowRight
} from 'lucide-react';

// ── Data ────────────────────────────────────────────────────────────────────
const achievements = [
  '30 years of continuous educational excellence',
  'Affiliated to Bihar Government Board',
  'Smart exam evaluation & OMR testing powered by ScanScore (scanscore.app)',
  'Fully equipped Computer & Science Labs',
  'Sports facilities: Cricket, Volleyball, Athletics',
  'Cultural programmes: Dance, Art, Music',
  'Regular parent-teacher interaction programmes',
];

const legacyGallery = [
  {
    title: '30 years of trusted service',
    image: '/descriptive-image/thirty-years-service-award.png',
    alt: 'Thirty years service award at Prabhat Tara Vidya Niketan'
  },
  {
    title: 'Celebrating board toppers',
    image: '/descriptive-image/school-toppers-class-x-congratulations-2.png',
    alt: 'Board topper congratulations visual for Prabhat Tara Vidya Niketan'
  },
];

// ── SVG Wave Divider ─────────────────────────────────────────────────────────
const WaveDivider = ({ fill = '#ffffff', flip = false }) => (
  <div className={`wave-divider${flip ? ' wave-flip' : ''}`} aria-hidden="true">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 72" preserveAspectRatio="none">
      <path fill={fill} d="M0,36 C240,72 480,0 720,36 C960,72 1200,0 1440,36 L1440,72 L0,72 Z" />
    </svg>
  </div>
);

const PencilSVG = () => (
  <svg className="deco-svg deco-pencil" viewBox="0 0 60 60" fill="none" aria-hidden="true">
    <path d="M46 6l8 8L18 50l-8 2 2-8 34-38z" fill="currentColor" opacity=".12" />
    <path d="M46 6l8 8" stroke="currentColor" strokeWidth="2" opacity=".25" />
    <path d="M10 52l2-8" stroke="currentColor" strokeWidth="2" opacity=".2" />
  </svg>
);

const StarSVG = () => (
  <svg className="deco-svg deco-star" viewBox="0 0 50 50" fill="none" aria-hidden="true">
    <path d="M25 4l5.6 11.3L44 17.3l-9.5 9.3 2.2 13.1L25 33.5l-11.7 6.2 2.2-13.1L6 17.3l13.4-2 5.6-11.3z"
      fill="currentColor" opacity=".15" />
  </svg>
);

// ── Component ────────────────────────────────────────────────────────────────
export default function Hero() {
  const { t } = useTranslation();
  const revealRef = useScrollReveal();

  const stats = [
    { Icon: GraduationCap, value: '1200+', label: t('stats_students') },
    { Icon: Users,          value: '50+',   label: t('stats_educators') },
    { Icon: Award,          value: '30+',   label: t('stats_years') },
    { Icon: Trophy,         value: '100+',  label: t('stats_awards') },
  ];

  const features = [
    { Icon: BookOpen,  title: t('feature_academic_title'),   desc: t('feature_academic_desc') },
    { Icon: Star,      title: t('feature_holistic_title'),  desc: t('feature_holistic_desc') },
    { Icon: Shield,    title: t('feature_safe_title'),       desc: t('feature_safe_desc') },
    { Icon: Palette,   title: t('feature_creative_title'),      desc: t('feature_creative_desc') },
    { Icon: Monitor,   title: t('feature_digital_title'), desc: t('feature_digital_desc') },
    { Icon: Heart,     title: t('feature_faculty_title'),         desc: t('feature_faculty_desc') },
  ];

  const showcaseCards = [
    {
      tag: t('showcase_1_tag'),
      title: t('showcase_1_title'),
      desc: t('showcase_1_desc'),
      image: '/descriptive-image/students-with-parliament-models1.png',
      alt: 'Students standing with parliament models at school'
    },
    {
      tag: t('showcase_2_tag'),
      title: t('showcase_2_title'),
      desc: t('showcase_2_desc'),
      image: '/descriptive-image/students-holding-certificates-drawings-school.jpg',
      alt: 'Students holding certificates and drawings at school'
    },
  ];

  const programs = [
    { name: t('program_preprimary'), range: t('program_preprimary_desc'), tag: 'Pre-School',   color: '#ff6b2b' },
    { name: t('program_primary'),    range: t('program_primary_desc'),    tag: 'Foundation',   color: '#27ae60' },
    { name: t('program_middle'),     range: t('program_middle_desc'),     tag: 'Intermediate', color: '#0c278c' },
    { name: t('program_high'),       range: t('program_high_desc'),       tag: 'Bihar Board',  color: '#9b27af' },
  ];

  return (
    <div className='hero' ref={revealRef}>
      <StructuredData type="home" />
      <NotificationMarquee />

      {/* ── Hero Video Banner ── */}
      <section className='hero-banner'>
        <div className='hero-shell'>
          <div className='hero-content' data-reveal='left'>
            <span className='hero-badge'>
              <GraduationCap size={14} /> {t('hero_admissions_badge')}
            </span>
            <h1 className='hero-title'>{t('school_name')}</h1>
            <p className='hero-subtitle'>{t('hero_tagline')}</p>
            <div className='hero-cta-group'>
              <a href='/admission-enquiry' className='cta-primary'>{t('apply_for_admission')}</a>
              <a href='/about-us' className='cta-secondary'>{t('explore_school')}</a>
            </div>
            <div className='hero-trust-row'>
              {[t('tag_bihar_board'), t('tag_years'), t('tag_digital_learning')].map(tag => (
                <span key={tag} className='trust-tag'>
                  <CheckCircle size={13} /> {tag}
                </span>
              ))}
            </div>
          </div>

          <div className='hero-media' data-reveal='right'>
            <div className='video-stage'>
              <div className='video-stage__meta'>
                <span className='video-stage__tag'>{t('welcome_film')}</span>
                <p>{t('welcome_film_desc')}</p>
              </div>
              <video
                src='/ptvn_hero.mp4'
                poster='/descriptive-image/school-building-front-view.jpg'
                autoPlay
                loop
                muted
                playsInline
                aria-label='Curtains opening to reveal the Prabhat Tara Vidya Niketan campus'
              />
            </div>
            <div className='video-caption'>
              <h2>{t('explore_campus_title')}</h2>
              <p>{t('explore_campus_desc')}</p>
            </div>
          </div>
        </div>

        <div className='hero-support-grid'>
          <div className='support-note' data-reveal>
            <span className='support-note__tag'>{t('why_parents_choose_title')}</span>
            <h3>{t('why_parents_choose_heading')}</h3>
            <ul className='support-note__list'>
              <li><CheckCircle size={16} /> {t('point_easy_admission')}</li>
              <li><CheckCircle size={16} /> {t('point_academics_holistic')}</li>
              <li><CheckCircle size={16} /> {t('point_parent_engagement')}</li>
            </ul>
          </div>

          <article className='support-card' data-reveal='right'>
            <div className='support-card__copy'>
              <span className='support-card__tag'>{t('our_campus_tag')}</span>
              <h3>{t('our_campus_heading')}</h3>
              <p>{t('our_campus_desc')}</p>
            </div>
            <img src='/descriptive-image/school-building-front-view.jpg' alt='Front view of the Prabhat Tara Vidya Niketan campus' loading='lazy' />
          </article>
        </div>

        <WaveDivider fill="#ffffff" />
      </section>

      {/* ── Stats ── */}
      <section className='stats-section'>
        <div className='stats-grid'>
          {stats.map(({ Icon, value, label }, i) => (
            <div key={i} className='stat-card' data-reveal data-reveal-delay={String(i + 1)}>
              <div className='stat-icon'><Icon size={34} strokeWidth={1.6} /></div>
              <span className='stat-value'>{value}</span>
              <span className='stat-label'>{label}</span>
            </div>
          ))}
        </div>
      </section>

      <WaveDivider fill="#f5f7ff" />

      {/* ── Director / Principal Messages ── */}
      <div className='section-bg-alt'>
        <MessageSection />
      </div>

      <WaveDivider fill="#ffffff" flip />

      {/* ── Why Choose Us ── */}
      <section className='why-us-section'>
        <PencilSVG />
        <StarSVG />
        <div className='section-header' data-reveal>
          <span className='section-tag'>{t('why_choose_tag')}</span>
          <h2>{t('why_choose_heading')}</h2>
          <p>{t('why_choose_subheading')}</p>
        </div>

        <div className='showcase-grid'>
          {showcaseCards.map(({ tag, title, desc, image, alt }, i) => (
            <article key={title} className='showcase-card' data-reveal data-reveal-delay={String(i + 1)}>
              <div className='showcase-card__media'>
                <img src={image} alt={alt} loading='lazy' />
              </div>
              <div className='showcase-card__body'>
                <span className='showcase-card__tag'>{tag}</span>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            </article>
          ))}
        </div>

        <div className='features-grid'>
          {features.map(({ Icon, title, desc }, i) => (
            <div key={i} className='feature-card' data-reveal data-reveal-delay={String(i + 1)}>
              <div className='feature-icon'><Icon size={26} strokeWidth={1.8} /></div>
              <h3>{title}</h3>
              <p>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <WaveDivider fill="#f0f4ff" />

      {/* ── Programs Offered ── */}
      <section className='programs-section'>
        <div className='section-header light' data-reveal>
          <span className='section-tag accent'>{t('programs_tag')}</span>
          <h2>{t('programs_heading')}</h2>
          <p>{t('programs_subheading')}</p>
        </div>
        <div className='programs-grid'>
          {programs.map(({ name, range, tag, color }, i) => (
            <div key={i} className='program-card' style={{ '--prog-color': color }} data-reveal data-reveal-delay={String(i + 1)}>
              <span className='prog-tag'>{tag}</span>
              <h3>{name}</h3>
              <p className='prog-range'>{range}</p>
              <a href='/fee-structure' className='prog-link'>
                {t('fee_structure')} <ArrowRight size={14} />
              </a>
            </div>
          ))}
        </div>
        <WaveDivider fill="#ffffff" flip />
      </section>

      {/* ── Achievements List ── */}
      <section className='achievements-section'>
        <div className='achievements-inner'>
          <div className='achievements-text' data-reveal='left'>
            <span className='section-tag'>{t('legacy_tag')}</span>
            <h2>{t('legacy_heading')}</h2>
            <p>{t('legacy_subheading')}</p>
            <ul className='achievements-list'>
              {achievements.map((item, i) => (
                <li key={i}><CheckCircle size={16} /> {item}</li>
              ))}
            </ul>
            <a href='/about-us' className='cta-primary'>{t('explore_school')}</a>
          </div>
          <div className='achievements-visual' data-reveal='right'>
            {legacyGallery.map(({ title, image, alt }, index) => (
              <figure key={title} className={`achievement-photo achievement-photo--${index === 0 ? 'primary' : 'secondary'}`}>
                <img src={image} alt={alt} loading='lazy' />
                <figcaption>{title}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <WaveDivider fill="#f5f7ff" />

      {/* ── Facebook Posts ── */}
      <div className='section-bg-alt'>
        <FacebookPagePosts />
      </div>

      <WaveDivider fill="#ffffff" flip />

      {/* ── Play Class ── */}
      <section className='playSection'>
        <img src="/descriptive-image/school-welcome-cartoon-showing-dont-worry-school-is-other-home-4-panel-story.png" alt="Illustrated welcome story for younger learners at Prabhat Tara Vidya Niketan" loading="lazy" />
        <div>
          <span className='play-badge'>{t('play_school_heading')}</span>
          <h2>{t('play_section')}</h2>
          <p className='lab-desc'>For younger students and new parents, the first school experience matters. PTVN pairs structured learning with a warm welcome that makes every child feel settled.</p>
          <a href='/admission-enquiry' className='play-link'>Enquire Now <ArrowRight size={16} /></a>
        </div>
      </section>

      {/* ── Student Care ── */}
      <section className='firstSection'>
        <div className='firstSection__copy' data-reveal='left'>
          <span className='section-tag accent'>Student Care</span>
          <h2>School life here is not only about marks, but also about wellbeing and everyday care.</h2>
          <p>Health drives, attentive routines, and a visibly involved school culture help families trust the campus beyond academics alone.</p>
          <a href='/about-us' className='play-link'>See More About Campus Life <ArrowRight size={16} /></a>
        </div>
        <div className='firstSection__media' data-reveal='right'>
          <img src="/descriptive-image/nation-deworming-day-celebration.png" alt="Nation deworming day celebration at Prabhat Tara Vidya Niketan" loading="lazy" />
        </div>
      </section>

      {/* ── Computer Lab ── */}
      <section className='playSection lab-section'>
        <img src="/descriptive-image/students-learning-with-laptops-classroom.png" alt="Students learning with laptops in the classroom at Prabhat Tara Vidya Niketan" loading="lazy" />
        <div>
          <h2>{t('computer_lab_heading')}</h2>
          <p className='lab-desc'>Our modern computer lab equips students with essential digital skills for the 21st century.</p>
        </div>
      </section>

      <AboutSchool />
      <Testimonials />
      <ContactUs />
    </div>
  );
}
